import { format } from 'date-fns'
import { Check, ExternalLink, Eye, FileText, Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ErrorNotice from '@/components/common/error-notice'
import ImageGalleryPreview from '@/components/common/ImageGalleryPreview'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import LoadingSpinner from '@/components/ui/icons/loading-spinner'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { isRoleAllowedToMarkFileViewed } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import {
  type PatientFile,
  useMarkPatientFileViewed,
  useOrderRequestIdMap,
  usePatientFiles
} from '@/services/patientFiles'

const formatFileSize = (sizeBytes: number | null): string | null => {
  if (sizeBytes == null) return null
  if (sizeBytes < 1024) return `${sizeBytes} B`
  if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)} KB`
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
}

const fileDisplayName = (file: PatientFile): string => file.originalFilename ?? `File ${file.id.slice(-6)}`

type FileBadgeVariant = 'neutral' | 'info' | 'success' | 'warning'

const fileBadgeVariants: Record<FileBadgeVariant, string> = {
  neutral: 'bg-white border-custom-neutral-200 text-custom-neutral-800',
  info: 'bg-blue-50 border-[#153CB7] text-[#153CB7]',
  success: 'bg-green-50 border-green-500 text-green-500',
  warning: 'bg-yellow-50 border-yellow-500 text-yellow-500'
}

function FileBadge({ variant, children }: { variant: FileBadgeVariant; children: React.ReactNode }) {
  return (
    <span className={cn('px-3 py-1 rounded-2xl border text-[13px] leading-5', fileBadgeVariants[variant])}>
      {children}
    </span>
  )
}

function ViewedBadge({ viewed }: { viewed: boolean }) {
  return <FileBadge variant={viewed ? 'success' : 'warning'}>{viewed ? 'Viewed' : 'Not viewed'}</FileBadge>
}

/**
 * Order provenance chip. When the order resolves to a med request (own or
 * linked-group history), the chip links to that request's page; the current
 * order and unresolvable orders stay plain chips.
 */
function OrderBadge({
  orderNumber,
  isCurrentOrder,
  orderRequestId
}: {
  orderNumber: string | null
  isCurrentOrder: boolean
  orderRequestId?: string
}) {
  if (!orderNumber) return <FileBadge variant='neutral'>No order</FileBadge>
  if (isCurrentOrder) return <FileBadge variant='info'>This order</FileBadge>
  if (!orderRequestId) return <FileBadge variant='neutral'>Order {orderNumber}</FileBadge>

  return (
    <Link
      to={`/request/${orderRequestId}`}
      title={`Open order ${orderNumber}`}
      data-testid='patient-file-order-link'
      className='rounded-2xl hover:underline underline-offset-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring'
    >
      <FileBadge variant='neutral'>Order {orderNumber}</FileBadge>
    </Link>
  )
}

function FileThumbnail({ file, onClick }: { file: PatientFile; onClick: () => void }) {
  const [hasError, setHasError] = useState(false)
  const isImage = file.mimeType.startsWith('image/')

  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={`Preview ${fileDisplayName(file)}`}
      className='group relative w-14 h-14 shrink-0 rounded-2xl border border-custom-neutral-200 bg-white overflow-hidden grid place-items-center cursor-pointer transition-colors hover:border-custom-neutral-400'
    >
      {isImage && !hasError ? (
        <img
          src={file.url}
          alt={fileDisplayName(file)}
          onError={() => setHasError(true)}
          className='w-full h-full object-cover'
        />
      ) : (
        <FileText className='w-6 h-6 text-custom-neutral-800' />
      )}
      <span className='absolute inset-0 grid place-items-center bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity'>
        <Eye className='w-5 h-5 text-white' />
      </span>
    </button>
  )
}

/**
 * Preview a file from the feed. Presigned URLs expire after 300 s, so the feed
 * is refetched on open and the fresh URL pinned in state — background feed
 * refreshes won't reload the viewer while it is up.
 *
 * The dialog shell (header + frame) renders immediately and the fresh-URL
 * loading state lives inside it, so opening a file never flashes a bare
 * spinner over the overlay.
 */
function PatientFilePreview({
  patientHash,
  file,
  onClose
}: {
  patientHash: string
  file: PatientFile
  onClose: () => void
}) {
  const { refetch } = usePatientFiles(patientHash)
  const [freshFile, setFreshFile] = useState<PatientFile | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh once per opened file
  useEffect(() => {
    let cancelled = false
    setFreshFile(null)
    refetch().then(({ data }) => {
      if (cancelled) return
      // On a failed refetch the URL we already hold is usually still inside its
      // expiry window — better to try it than to show nothing.
      setFreshFile(data?.find(candidate => candidate.id === file.id) ?? file)
    })
    return () => {
      cancelled = true
    }
  }, [file.id])

  if (freshFile?.mimeType.startsWith('image/')) {
    return <ImageGalleryPreview images={freshFile.url} onClose={onClose} />
  }

  const displayName = fileDisplayName(file)
  const isPdf = freshFile?.mimeType === 'application/pdf'
  // The URL from the feed is usually still inside its expiry window — the
  // header action works while the fresh URL loads.
  const openInNewTab = () => window.open(freshFile?.url ?? file.url, '_blank', 'noopener,noreferrer')

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        hideCloseButton
        className='w-[90vw] max-w-7xl h-[85vh] z-91 bg-white border-none rounded-2xl sm:rounded-2xl flex flex-col gap-4'
      >
        <div className='flex items-center justify-between gap-4'>
          <DialogTitle className='text-custom-neutral-900 text-base font-semibold leading-6 truncate'>
            {displayName}
          </DialogTitle>
          <DialogDescription className='sr-only'>Preview of {displayName}</DialogDescription>
          <div className='flex items-center gap-1 shrink-0'>
            <button
              type='button'
              onClick={openInNewTab}
              title='Open in new tab'
              aria-label='Open in new tab'
              className='h-8 w-8 grid place-items-center rounded-full text-custom-neutral-600 transition-colors hover:bg-custom-neutral-200/60'
            >
              <ExternalLink className='w-4 h-4' />
            </button>
            <button
              type='button'
              onClick={onClose}
              title='Close'
              aria-label='Close'
              className='h-8 w-8 grid place-items-center rounded-full text-custom-neutral-600 transition-colors hover:bg-custom-neutral-200/60'
            >
              <X className='w-4 h-4' />
            </button>
          </div>
        </div>

        {!freshFile ? (
          <div className='flex-1 min-h-0 grid place-items-center'>
            <LoadingSpinner iconClassName='w-10 h-10 text-custom-neutral-200 animate-spin' />
          </div>
        ) : isPdf ? (
          <iframe
            src={freshFile.url}
            title={displayName}
            className='w-full flex-1 min-h-0 border border-custom-neutral-200 rounded-lg'
          />
        ) : (
          <div className='flex-1 min-h-0 flex flex-col items-center justify-center gap-4'>
            <p className='text-custom-neutral-800 text-sm leading-[170%]'>
              This file can't be previewed in the browser.
            </p>
            <Button type='button' variant='outline' className='rounded-2xl' onClick={openInNewTab}>
              <ExternalLink className='w-4 h-4 mr-2' />
              Open in new tab
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function FileRow({
  file,
  isCurrentOrder,
  orderRequestId,
  canMarkViewed,
  isMarking,
  onPreview,
  onMarkViewed
}: {
  file: PatientFile
  isCurrentOrder: boolean
  orderRequestId?: string
  canMarkViewed: boolean
  isMarking: boolean
  onPreview: () => void
  onMarkViewed: () => void
}) {
  const meta = [
    file?.uploadedBy ? `Uploaded by ${file.uploadedBy}` : 'Unknown',
    format(new Date(file.createdAt), 'dd MMM yyyy, HH:mm'),
    formatFileSize(file.sizeBytes)
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div
      data-testid='patient-file-row'
      className='flex items-center gap-4 rounded-3xl border border-custom-neutral-200 bg-[#FAFAFB] px-6 py-4 transition-colors hover:border-custom-neutral-400'
    >
      <FileThumbnail key={file.url} file={file} onClick={onPreview} />

      <div className='flex flex-col gap-1 min-w-0 flex-1'>
        <button
          type='button'
          onClick={onPreview}
          title={fileDisplayName(file)}
          className='text-left text-[#1D1D1D] text-sm font-bold leading-[170%] truncate hover:underline cursor-pointer'
        >
          {fileDisplayName(file)}
        </button>
        <p className='text-custom-neutral-800 text-sm leading-[170%] truncate'>{meta}</p>
        <div className='flex items-center gap-2 flex-wrap'>
          <OrderBadge orderNumber={file.orderNumber} isCurrentOrder={isCurrentOrder} orderRequestId={orderRequestId} />
          <ViewedBadge viewed={file.viewedByPrescriber} />
        </div>
      </div>

      <div className='flex items-center gap-2 shrink-0'>
        <Button
          type='button'
          variant='outline'
          className='rounded-2xl'
          onClick={onPreview}
          data-testid='patient-file-view'
        >
          <Eye className='w-4 h-4 mr-2' />
          View
        </Button>
        {canMarkViewed && !file.viewedByPrescriber && (
          <Button
            type='button'
            className='rounded-2xl'
            disabled={isMarking}
            onClick={onMarkViewed}
            data-testid='patient-file-mark-viewed'
          >
            {isMarking ? (
              <>
                <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                Marking…
              </>
            ) : (
              <>
                <Check className='w-4 h-4 mr-2' />
                Mark as viewed
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

function FileRowSkeleton() {
  return (
    <div className='flex items-center gap-4 rounded-3xl border border-custom-neutral-200 bg-[#FAFAFB] px-6 py-4 animate-pulse'>
      <div className='w-14 h-14 shrink-0 rounded-2xl bg-custom-neutral-200' />
      <div className='flex flex-col gap-2 min-w-0 flex-1'>
        <div className='h-4 w-1/3 rounded bg-custom-neutral-200' />
        <div className='h-4 w-1/2 rounded bg-custom-neutral-200' />
        <div className='h-[26px] w-44 rounded-2xl bg-custom-neutral-200' />
      </div>
      <div className='hidden sm:block h-12 w-24 shrink-0 rounded-2xl bg-custom-neutral-200' />
    </div>
  )
}

/**
 * The patient's file feed (own + linked group, across all orders). Every file
 * must be marked viewed before the request can be actioned — the footer reads
 * the same query through usePatientFilesGate.
 */
export default function PatientFiles() {
  const medRequest = useCurrentMedication()
  const data = medRequest?.data
  const patientHash = data?.patient?.id

  const { data: files, isPending, isError, refetch } = usePatientFiles(patientHash)
  const markViewed = useMarkPatientFileViewed(patientHash)
  const orderRequestIdMap = useOrderRequestIdMap(patientHash, data?.patient?.linkedPatientHash)
  const canMarkViewed = isRoleAllowedToMarkFileViewed()
  const [previewFile, setPreviewFile] = useState<PatientFile | null>(null)

  if (!patientHash) return null

  // Zero files → nothing to review and the gate is open; hide like the other
  // data-driven sections.
  if (!isPending && !isError && !files?.length) return null

  const viewedCount = files?.filter(file => file.viewedByPrescriber).length ?? 0
  const allViewed = !!files?.length && viewedCount === files.length

  return (
    <div id='patient-files' data-testid='patient-files-section' className='flex flex-col gap-6'>
      <div className='flex items-center gap-3'>
        <h2 className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Patient Files</h2>
        {!!files?.length && (
          <FileBadge variant={allViewed ? 'success' : 'warning'}>
            {viewedCount} of {files.length} viewed
          </FileBadge>
        )}
      </div>

      {isPending ? (
        <div className='flex flex-col gap-4'>
          <FileRowSkeleton />
          <FileRowSkeleton />
        </div>
      ) : isError ? (
        <div className='flex flex-col items-start gap-4 rounded-3xl border border-[#FF8080] p-6'>
          <ErrorNotice
            title='Patient files could not be loaded'
            description='Approve, Follow Up and Reject stay unavailable until the files can be reviewed.'
          />
          <Button type='button' variant='outline' className='rounded-2xl' onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <div className='flex flex-col gap-4'>
          {files?.map(file => {
            const isCurrentOrder = !!file.orderNumber && file.orderNumber === data?.orderNumber
            return (
              <FileRow
                key={file.id}
                file={file}
                isCurrentOrder={isCurrentOrder}
                orderRequestId={
                  !isCurrentOrder && file.orderNumber ? orderRequestIdMap.get(file.orderNumber) : undefined
                }
                canMarkViewed={canMarkViewed}
                isMarking={markViewed.isPending && markViewed.variables === file.id}
                onPreview={() => setPreviewFile(file)}
                onMarkViewed={() => markViewed.mutate(file.id)}
              />
            )
          })}
        </div>
      )}

      {previewFile && (
        <PatientFilePreview patientHash={patientHash} file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
    </div>
  )
}
